import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import IOSSwitch from 'src/components/switch/switch.component';
import StatusSwitch from 'src/components/switch/status-switch.component';
import { defaultCollection, DomainSummary, getHelpBaseUrl, KBSetupShell } from './kbSetupShared';

export default function KBCollectionsSetup() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const [loader, setLoader] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [kbSettings, setKBSettings] = useState({ is_enabled: true });
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionForm, setCollectionForm] = useState(defaultCollection);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canManageKB = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.KB_MANAGE);
  const helpBaseUrl = useMemo(() => getHelpBaseUrl(), []);
  const setupDisabled = !kbSettings.is_enabled;
  const disabledContentSx = setupDisabled
    ? {
        opacity: 0.45,
        filter: 'grayscale(0.35)',
        transition: 'opacity 180ms ease, filter 180ms ease',
      }
    : {
        opacity: 1,
        transition: 'opacity 180ms ease, filter 180ms ease',
      };
  const toggleInFlightRef = useRef(false);

  const fetchCollections = useCallback(() => {
    if (!canManageKB) {
      setLoader(false);
      return;
    }
    setLoader(true);
    HttpServices.Get({
      url: userModuleURL.kbCollections,
      successCallback: (collectionData) => {
        setCollections(Array.isArray(collectionData) ? collectionData : []);
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        message && showToast(message, 'e');
      },
    });
  }, [canManageKB, showToast]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const fetchKBSettings = useCallback(() => {
    if (!canManageKB) return;

    HttpServices.Get({
      url: userModuleURL.kbSettings,
      successCallback: (data) => {
        setKBSettings({ is_enabled: data?.is_enabled !== false });
      },
      failureCallback: (message) => {
        message && showToastRef.current(message, 'e');
      },
    });
  }, [canManageKB]);

  useEffect(() => {
    fetchKBSettings();
  }, [fetchKBSettings]);

  const toggleKBSettings = useCallback(
    (event) => {
      if (toggleInFlightRef.current) return;

      const nextEnabled = event.target.checked;
      let previousEnabled = true;

      setKBSettings((prev) => {
        previousEnabled = Boolean(prev.is_enabled);
        return prev.is_enabled === nextEnabled ? prev : { ...prev, is_enabled: nextEnabled };
      });

      toggleInFlightRef.current = true;
      setIsSavingSettings(true);

      HttpServices.Post({
        url: userModuleURL.kbSettings,
        body: { is_enabled: nextEnabled },
        successCallback: (data, message) => {
          const enabled = data?.is_enabled !== false;
          setKBSettings((prev) =>
            prev.is_enabled === enabled ? prev : { ...prev, is_enabled: enabled }
          );
          toggleInFlightRef.current = false;
          setIsSavingSettings(false);
          message && showToastRef.current(message, 's');
        },
        failureCallback: (message) => {
          setKBSettings((prev) =>
            prev.is_enabled === previousEnabled ? prev : { ...prev, is_enabled: previousEnabled }
          );
          toggleInFlightRef.current = false;
          setIsSavingSettings(false);
          message && showToastRef.current(message, 'e');
        },
      });
    },
    []
  );

  const headerAction = useMemo(() => {
    if (!canManageKB) return null;

    return (
      <StatusSwitch
        checked={Boolean(kbSettings.is_enabled)}
        onChange={toggleKBSettings}
        inputProps={{
          'aria-label': kbSettings.is_enabled
            ? translate('kb_setup_page.disable_kb', { defaultValue: 'Disable Knowledge Base' })
            : translate('kb_setup_page.enable_kb', { defaultValue: 'Enable Knowledge Base' }),
        }}
        switchSx={{
          opacity: isSavingSettings ? 0.7 : 1,
          transition: 'opacity 0.15s ease',
        }}
      />
    );
  }, [canManageKB, isSavingSettings, kbSettings.is_enabled, toggleKBSettings, translate]);

  const copyHelpDomain = () => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(helpBaseUrl);
    showToast(translate('kb_setup_page.domain_copied', { defaultValue: 'Knowledge base URL copied' }), 's');
  };

  const openAddCollectionDialog = () => {
    if (setupDisabled) return;
    setCollectionForm(defaultCollection);
    setCollectionDialogOpen(true);
  };

  const closeCollectionDialog = () => {
    if (saveLoader) return;
    setCollectionDialogOpen(false);
    setCollectionForm(defaultCollection);
  };

  const editCollection = (collection) => {
    if (setupDisabled) return;
    setCollectionForm({
      id: collection.id,
      name: collection.name || '',
      slug: collection.slug || '',
      description: collection.description || '',
      sort_order: collection.sort_order ?? '',
      is_public: collection.is_public,
      is_enabled: collection.is_enabled,
    });
    setCollectionDialogOpen(true);
  };

  const saveCollection = () => {
    if (setupDisabled) return;
    if (!collectionForm.name.trim()) {
      showToast(translate('kb_setup_page.collection_name_required', { defaultValue: 'Collection name is required' }), 'e');
      return;
    }
    setSaveLoader(true);
    HttpServices.Post({
      url: userModuleURL.kbCollections,
      body: {
        ...collectionForm,
        sort_order: collectionForm.sort_order === '' ? null : Number(collectionForm.sort_order),
      },
      successCallback: (_data, message) => {
        setCollectionForm(defaultCollection);
        setCollectionDialogOpen(false);
        setSaveLoader(false);
        fetchCollections();
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setSaveLoader(false);
        message && showToast(message, 'e');
      },
    });
  };

  const deleteCollection = () => {
    if (!deleteTarget || setupDisabled) return;
    setDeleteLoader(true);
    HttpServices.Delete({
      url: `${userModuleURL.kbCollections}/${deleteTarget.id}`,
      successCallback: (_data, message) => {
        if (collectionForm.id === deleteTarget.id) {
          setCollectionForm(defaultCollection);
        }
        fetchCollections();
        setDeleteLoader(false);
        setDeleteTarget(null);
        message && showToast(message, 's');
      },
      failureCallback: (message) => {
        setDeleteLoader(false);
        message && showToast(message, 'e');
      },
    });
  };

  const renderCollectionEditor = () => (
    <Stack spacing={1.5}>
      <TextField fullWidth disabled={setupDisabled} label={translate('kb_setup_page.name', { defaultValue: 'Name' })} value={collectionForm.name} onChange={(e) => setCollectionForm((prev) => ({ ...prev, name: e.target.value }))} />
      <TextField
        fullWidth
        disabled={setupDisabled}
        label={translate('kb_setup_page.slug', { defaultValue: 'Slug' })}
        value={collectionForm.slug}
        helperText={translate('kb_setup_page.slug_help', {
          defaultValue: 'Lowercase letters, numbers, and hyphens only. Leave empty to auto-generate.',
        })}
        onChange={(e) => setCollectionForm((prev) => ({ ...prev, slug: e.target.value }))}
      />
      <TextField
        fullWidth
        disabled={setupDisabled}
        type="number"
        label={translate('kb_setup_page.sort_order', { defaultValue: 'Sort Order' })}
        value={collectionForm.sort_order}
        onChange={(e) => setCollectionForm((prev) => ({ ...prev, sort_order: e.target.value }))}
      />
      <TextField fullWidth disabled={setupDisabled} multiline minRows={4} label={translate('kb_setup_page.description', { defaultValue: 'Description' })} value={collectionForm.description} onChange={(e) => setCollectionForm((prev) => ({ ...prev, description: e.target.value }))} />
      <Stack direction="row" spacing={2} alignItems="center">
        <IOSSwitch disabled={setupDisabled} checked={Boolean(collectionForm.is_public)} onChange={(e) => setCollectionForm((prev) => ({ ...prev, is_public: e.target.checked }))} />
        <Typography variant="body2">{translate('kb_setup_page.public', { defaultValue: 'Public' })}</Typography>
        <IOSSwitch disabled={setupDisabled} checked={Boolean(collectionForm.is_enabled)} onChange={(e) => setCollectionForm((prev) => ({ ...prev, is_enabled: e.target.checked }))} />
        <Typography variant="body2">{translate('common.enabled', { defaultValue: 'Enabled' })}</Typography>
      </Stack>
    </Stack>
  );

  return (
    <KBSetupShell activeView="collections" title={translate('kb_setup_page.title', { defaultValue: 'Knowledge Base Setup' })} headerAction={headerAction}>
      {loader ? <LinearProgress /> : !canManageKB ? (
        <Typography variant="body2" color="text.secondary">
          {translate('kb_setup_page.permission_note', {
            defaultValue: 'Knowledge base setup is limited to users with KB manage access.',
          })}
        </Typography>
      ) : (
        <Stack spacing={2}>
          <DomainSummary helpBaseUrl={helpBaseUrl} disabled={setupDisabled} onCopy={copyHelpDomain} />
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, ...disabledContentSx }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Box>
                  <Typography variant="subtitle1">
                    {translate('kb_setup_page.collections_title', { defaultValue: 'Collections' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('kb_setup_page.collections_help', {
                      defaultValue: 'Group related knowledge base articles and control what visitors can see.',
                    })}
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<CollectionsBookmarkIcon />} disabled={setupDisabled} onClick={openAddCollectionDialog}>
                  {translate('kb_setup_page.add_collection', { defaultValue: 'Add Collection' })}
                </Button>
              </Stack>
              {collections.map((collection) => (
                <Paper key={collection.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{collection.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {collection.description ||
                          translate('kb_setup_page.no_description', { defaultValue: 'No description' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translate('kb_setup_page.slug_prefix', { defaultValue: 'Slug' })}: {collection.slug} |{' '}
                        {translate('kb_setup_page.sort_prefix', { defaultValue: 'Sort' })}: {collection.sort_order}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={collection.is_public ? translate('kb_setup_page.public', { defaultValue: 'Public' }) : translate('kb_setup_page.private', { defaultValue: 'Private' })} />
                      <Chip size="small" color={collection.is_enabled ? 'primary' : 'default'} label={collection.is_enabled ? translate('common.enabled', { defaultValue: 'Enabled' }) : translate('common.disabled', { defaultValue: 'Disabled' })} />
                      <Button size="small" variant="outlined" disabled={setupDisabled} onClick={() => editCollection(collection)}>{translate('common.edit', { defaultValue: 'Edit' })}</Button>
                      <Button size="small" color="error" variant="outlined" disabled={setupDisabled} onClick={() => setDeleteTarget(collection)}>{translate('common.delete', { defaultValue: 'Delete' })}</Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      <Dialog open={collectionDialogOpen} onClose={closeCollectionDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {collectionForm.id
            ? translate('kb_setup_page.edit_collection', { defaultValue: 'Edit Collection' })
            : translate('kb_setup_page.add_collection', { defaultValue: 'Add Collection' })}
        </DialogTitle>
        <DialogContent dividers>{renderCollectionEditor()}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="text" onClick={closeCollectionDialog} disabled={saveLoader}>{translate('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <LoadingButton variant="contained" loading={saveLoader} disabled={setupDisabled} onClick={saveCollection}>{translate('kb_setup_page.save_collection', { defaultValue: 'Save Collection' })}</LoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoader && setDeleteTarget(null)}
        onConfirm={deleteCollection}
        title={translate('common.confirm_deletion', { defaultValue: 'Confirm deletion' })}
        message={translate('kb_setup_page.delete_collection_message', {
          name: deleteTarget?.name,
          defaultValue: 'Delete collection "{{name}}"? Articles in this collection will be unassigned.',
        })}
        submitButtonName={translate('common.delete', { defaultValue: 'Delete' })}
        loading={deleteLoader}
      />
    </KBSetupShell>
  );
}
